import {
  getNetSuiteSalesOrder,
  getNetSuitePurchaseOrder,
  submitPickingSalesOrder,
  submitReceivingPurchaseOrder
} from 'api'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useLocation } from 'react-router-dom'
import {
  isObjectNotEmpty,
  playErrorAlertAudio,
  setElementToTop,
  getCompanyFromPathname
} from 'utils'
import {
  companyOptions,
  DailyOrderType,
  ItemScanError,
  OperationPath,
  SnackbarSeverity
} from 'constant'
import {
  getNetSuiteTransferOrder,
  getNetSuiteTransferOrderFba,
  getProductBin,
  printFBALabel,
  submitPickingTransferOrder
} from 'api/netsuite'
import { addRedisTransferOrder } from 'api/redis'
import { SnackContext } from './SnackContextProvider'
import useDailyOrder from 'hooks/useDailyOrder'
import { LoadingContext } from './LoadingContextProvider'

interface OrderContext {
  getOrderInformation: (orderNumber: string) => void
  orderSearchError: boolean
  setOrderSearchError: (error: boolean) => void
  handleScanProduct: (upc: string, setItemUpc: (upc: string) => void) => void
  itemSearchError: string
  setItemSearchError: (itemSearchError: string) => void
  orderContents: any
  setOrderContents: (data: any) => void
  products: any[]
  handleSubmitPickingSalesOrder: (
    setOpenDialog: (open: boolean) => void,
    setLoading: (loading: boolean) => void
  ) => void
  handleSubmitPurchaseOrderReceiving: (
    setOpenDialog: (open: boolean) => void,
    setLoading: (loading: boolean) => void
  ) => void
  orderNumber: string
  setOrderNumber: (orderNumber: string) => void
  inputRef: any
  company: string
  handleGetProductBin: (product: any) => void
  handleSubmitPickingTransferOrder: (
    setOpenDialog: (open: boolean) => void,
    setLoading: (loading: boolean) => void
  ) => void
  handleSubmitScanningTOFBALabel: (
    setOpenDialog: (open: boolean) => void,
    setLoading: (loading: boolean) => void
  ) => void
  dailyOrders: any[]
  handleFetchDailyOrders: (
    company: string,
    dailyOrderType: DailyOrderType,
    boxPrefix?: string,
    skipBoxLookup?: boolean
  ) => Promise<any>
  selectedDailyOrderClass: string | undefined
  setSelectedDailyOrderClass: (className: string | undefined) => void
  filteredDailyOrdersByClass: any[]
  selectedDailyOrderBatchIndex: number | undefined
  setSelectedDailyOrderBatchIndex: (index: number | undefined) => void
  handleScanDailyOrderItem: (
    scannedValue: string,
    selectedDailyOrder: any,
    setScannedValue: (value: string) => void,
    setSelectedDailyOrder: (order: any) => void
  ) => void
  markDailyOrderAsFinished: (orderNumber: string) => void
  selectedLocationId: number | null
  setSelectedLocationId: React.Dispatch<React.SetStateAction<number | null>>
}

export const OrderContext = createContext<OrderContext>({
  getOrderInformation: () => {},
  orderSearchError: false,
  setOrderSearchError: () => {},
  handleScanProduct: () => {},
  itemSearchError: '',
  setItemSearchError: () => {},
  orderContents: {},
  setOrderContents: () => {},
  products: [],
  handleSubmitPickingSalesOrder: () => {},
  handleSubmitPurchaseOrderReceiving: () => {},
  orderNumber: '',
  setOrderNumber: () => {},
  inputRef: {},
  company: '',
  handleGetProductBin: () => {},
  handleSubmitPickingTransferOrder: () => {},
  handleSubmitScanningTOFBALabel: () => {},
  dailyOrders: [],
  handleFetchDailyOrders: () => Promise.resolve(),
  selectedDailyOrderClass: undefined,
  setSelectedDailyOrderClass: () => {},
  filteredDailyOrdersByClass: [],
  selectedDailyOrderBatchIndex: undefined,
  setSelectedDailyOrderBatchIndex: () => {},
  handleScanDailyOrderItem: () => {},
  markDailyOrderAsFinished: () => {},
  selectedLocationId: null,
  setSelectedLocationId: () => {}
})
interface Props {
  children: ReactNode
}
const OrderContextProvider = ({ children }: Props) => {
  const { pathname } = useLocation()
  const [orderSearchError, setOrderSearchError] = useState(false)
  const [itemSearchError, setItemSearchError] = useState('')
  const [orderNumber, setOrderNumber] = useState('')
  const [orderContents, setOrderContents] = useState<any>({})
  const [dailyOrders, setDailyOrders] = useState<any>([])
  const [selectedDailyOrderClass, setSelectedDailyOrderClass] = useState<
    string | undefined
  >(undefined)
  const [selectedDailyOrderBatchIndex, setSelectedDailyOrderBatchIndex] =
    useState<number | undefined>(undefined)
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null
  )
  const { handleOpenSnackbar } = useContext(SnackContext)
  const { setLoading } = useContext(LoadingContext)
  const {
    fetchDailyOrders,
    groupAndSortDailyOrdersByClass,
    scanDailyOrderItem,
    removeCurrentUserCache
  } = useDailyOrder()
  const company = getCompanyFromPathname(pathname, companyOptions)
  const inputRef = useRef(null)
  const products = isObjectNotEmpty(orderContents) ? orderContents.item : []
  const handleFetchDailyOrders = async (
    company: string,
    dailyOrderType: DailyOrderType,
    boxPrefix?: string,
    skipBoxLookup?: boolean
  ) => {
    setLoading(true)
    try {
      const result = await fetchDailyOrders(
        company,
        dailyOrderType,
        boxPrefix,
        skipBoxLookup
      )
      const sortedOrders = groupAndSortDailyOrdersByClass(result)
      setDailyOrders(sortedOrders)
      return sortedOrders
    } catch (error) {
      console.error('Failed to fetch daily orders:', error)
      alert(error)
    } finally {
      setLoading(false)
    }
  }
  const handleScanDailyOrderItem = (
    scannedValue: string,
    selectedDailyOrder: any,
    setScannedValue: (value: string) => void,
    setSelectedDailyOrder: (order: any) => void
  ) => {
    scanDailyOrderItem(
      scannedValue,
      selectedDailyOrder,
      setScannedValue,
      setSelectedDailyOrder,
      handleOpenSnackbar
    )
  }

  const markDailyOrderAsFinished = (orderNumber: string) => {
    const normalized = `${orderNumber || ''}`.trim().toLowerCase()
    if (!normalized) return
    setDailyOrders((prevOrders: any[]) =>
      prevOrders.map(group => {
        const updatedOrders = (group.orders || []).map((order: any) => {
          const sameOrder =
            `${order?.orderNumber || ''}`.trim().toLowerCase() === normalized
          if (!sameOrder) return order
          const updatedItems = (order.orderItems || []).map((item: any) => ({
            ...item,
            quantityRemaining: 0,
            quantityPicked:
              Number(item.quantity) || Number(item.quantityPicked) || 0
          }))
          return {
            ...order,
            orderItems: updatedItems
          }
        })
        const withIndex = updatedOrders.map((order: any, index: number) => ({
          order,
          index
        }))
        withIndex.sort((a: any, b: any) => {
          const aPicked = (a.order.orderItems || []).every(
            (item: any) => Number(item.quantityRemaining) <= 0
          )
          const bPicked = (b.order.orderItems || []).every(
            (item: any) => Number(item.quantityRemaining) <= 0
          )
          if (aPicked !== bPicked) return aPicked ? 1 : -1
          return a.index - b.index
        })
        return {
          ...group,
          orders: withIndex.map((entry: any) => entry.order)
        }
      })
    )
  }

  useEffect(() => {
    if (!selectedDailyOrderClass) return
    const stillValid = dailyOrders.some(
      (g: any) => g.class === selectedDailyOrderClass
    )
    if (!stillValid) {
      setSelectedDailyOrderClass(undefined)
      setSelectedDailyOrderBatchIndex(undefined)
    }
  }, [dailyOrders])

  const filteredDailyOrdersByClass = useMemo(() => {
    if (!selectedDailyOrderClass) return []
    const filteredOrders = dailyOrders.filter(
      (order: any) => order.class === selectedDailyOrderClass
    )
    return filteredOrders[0]?.orders ?? []
  }, [dailyOrders, selectedDailyOrderClass])

  const getOrderInformation = async (orderNumber: string) => {
    setLoading(true)
    let result: any
    try {
      if (pathname.includes(OperationPath.SalesOrderPicking)) {
        result = await getNetSuiteSalesOrder(orderNumber, company)
      } else if (pathname.includes(OperationPath.PurchaseOrderReceiving)) {
        result = await getNetSuitePurchaseOrder(orderNumber, company)
      } else if (pathname.includes(OperationPath.TransferOrderPicking)) {
        result = await getNetSuiteTransferOrder(orderNumber, company)
      } else if (pathname.includes(OperationPath.TransferOrderFBAPrint)) {
        result = await getNetSuiteTransferOrderFba(orderNumber, company)
      } else {
        return
      }
      if (result.data.success) {
        setLoading(false)
        setOrderContents(result.data.data)
        if (
          pathname.includes(OperationPath.TransferOrderPicking) ||
          pathname.includes(OperationPath.TransferOrderFBAPrint)
        ) {
          setOrderNumber(result.data.data?.tranid ?? orderNumber)
        }
      } else {
        setLoading(false)
        setOrderSearchError(true)
      }
    } catch (err) {
      setLoading(false)
      setOrderSearchError(true)
      console.error(err)
    }
  }

  const handleGetProductBin = async (product: any) => {
    try {
      const result = await getProductBin(
        product.item.links[0].href,
        orderContents.location.id,
        company
      )
      product.preferredBin = result.data.preferredBin
      const targetProductIndex = orderContents.item.findIndex(
        (item: any) => item.upcCode === product.upcCode
      )
      orderContents.item[targetProductIndex] = product
      setOrderContents({ ...orderContents })
    } catch (error) {
      console.error(error)
    }
  }
  const handleScanProduct = async (
    productUpc: string,
    setItemUpc: (productUpc: string) => void
  ) => {
    if (
      products.findIndex(
        (product: any) =>
          product.upcCode === productUpc || product.item.refName === productUpc
      ) === -1
    ) {
      playErrorAlertAudio()
      setItemSearchError(ItemScanError.InvalidUpc)
      return
    }
    const targetProduct: any = products.find(
      (product: any) =>
        (product.upcCode === productUpc ||
          product.item.refName === productUpc) &&
        product.quantityRemaining > 0
    )
    if (targetProduct) {
      const productLine = targetProduct.line
      setItemSearchError('')
      if (
        pathname.includes(OperationPath.SalesOrderPicking) ||
        pathname.includes(OperationPath.TransferOrderPicking)
      ) {
        if (!targetProduct.preferredBin) {
          targetProduct.preferredBin = 'loading'
        }
        if (targetProduct.quantityAvailable > 0) {
          targetProduct.quantityRemaining--
          targetProduct.quantityPicked++
          targetProduct.quantityAvailable--
        } else {
          playErrorAlertAudio()
          setItemSearchError(ItemScanError.NoInventory)
          return
        }
      } else if (pathname.includes(OperationPath.PurchaseOrderReceiving)) {
        targetProduct.quantityRemaining--
        targetProduct.quantityReceived++
      } else if (pathname.includes(OperationPath.TransferOrderFBAPrint)) {
        targetProduct.quantityRemaining--
        targetProduct.quantityPicked++
        const { FBANumber } = orderContents
        try {
          await printFBALabel(FBANumber, targetProduct.item.refName)
        } catch (error) {
          alert(error)
        }
      }
      const updatedProducts = [...products]
      const targetProductIndex = updatedProducts.findIndex(
        (product: any) => product.line === productLine
      )
      setElementToTop(updatedProducts, targetProductIndex)
      const newOrderContents = {
        ...orderContents,
        item: updatedProducts
      }
      setOrderContents(newOrderContents)
      if (pathname.includes(OperationPath.TransferOrderFBAPrint)) {
        addRedisTransferOrder(company, newOrderContents).catch(err =>
          console.error('Failed to persist FBA scan to Redis:', err)
        )
      }
      setItemUpc('')
    } else {
      playErrorAlertAudio()
      setItemSearchError(ItemScanError.ExceedMax)
    }
  }
  const handleSubmitPickingSalesOrder = async (
    setOpenDialog: (open: boolean) => void,
    setLoading: (loading: boolean) => void
  ) => {
    setLoading(true)
    const pickedItems = orderContents.item
      .filter((item: any) => {
        return (item.quantityPicked || 0) > 0 && item.line != null
      })
      .map((item: any) => {
        return {
          name: item.item.refName,
          itemId: item.item.id,
          line: item.line,
          quantityPicked: item.quantityPicked || 0
        }
      })
    try {
      const result = await submitPickingSalesOrder(
        orderContents.tranid,
        pickedItems,
        orderContents.location.id,
        company
      )
      if (result.data.success) {
        removeCurrentUserCache(orderNumber, company)
        setLoading(false)
        setOrderContents({})
        setOrderNumber('')
        setOpenDialog(false)
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          'Order picked successfully'
        )
      } else {
        setLoading(false)
        setOpenDialog(false)
        handleOpenSnackbar(SnackbarSeverity.Error, result.data.message)
      }
    } catch (error: any) {
      setLoading(false)
      setOpenDialog(false)
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        error.response.data.errorMessage || error.message
      )
    }
  }

  const handleSubmitPickingTransferOrder = async (
    setOpenDialog: (open: boolean) => void,
    setLoading: (loading: boolean) => void
  ) => {
    setLoading(true)
    const pickedItems = orderContents.item.map((item: any) => {
      return {
        itemId: item.item.id,
        quantityPicked: item.quantityPicked || 0,
        line: item.line
      }
    })
    try {
      const result = await submitPickingTransferOrder(
        orderContents.createdfrom.internalid,
        pickedItems,
        company
      )
      if (result.data.success) {
        removeCurrentUserCache(orderNumber, company)
        setLoading(false)
        setOrderContents({})
        setOrderNumber('')
        setOpenDialog(false)
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          'Order picked successfully'
        )
      }
    } catch (error: any) {
      setLoading(false)
      setOpenDialog(false)
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        error.response.data.errorMessage || error.message
      )
    }
  }

  const handleSubmitPurchaseOrderReceiving = async (
    setOpenDialog: (open: boolean) => void,
    setLoading: (loading: boolean) => void
  ) => {
    setLoading(true)
    const receivedItems = orderContents.item.map((item: any) => {
      return {
        line: item.line,
        quantityReceived: item.quantityReceived || 0,
        quantityRemaining: item.quantityRemaining,
        item: { id: item.item.id, refName: item.item.refName }
      }
    })
    try {
      const result = await submitReceivingPurchaseOrder(
        receivedItems,
        orderContents.location,
        company,
        orderContents.id
      )
      if (result.data.success) {
        removeCurrentUserCache(orderNumber, company)
        setLoading(false)
        setOrderContents({})
        setOrderNumber('')
        setOpenDialog(false)
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          'Order received successfully'
        )
      }
    } catch (error: any) {
      setLoading(false)
      setOpenDialog(false)
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        error.response.data.errorMessage || error.message
      )
    }
  }

  const handleSubmitScanningTOFBALabel = async (
    setOpenDialog: (open: boolean) => void,
    setLoading: (loading: boolean) => void
  ) => {
    try {
      const result = await addRedisTransferOrder(company, orderContents)
      if (result.data.success) {
        setLoading(false)
        setOrderContents({})
        setOrderNumber('')
        setOpenDialog(false)
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          'FBA Label added successfully'
        )
      }
    } catch (error: any) {
      setLoading(false)
      setOpenDialog(false)
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        error.response.data.errorMessage || error.message
      )
    }
  }

  return (
    <OrderContext.Provider
      value={{
        getOrderInformation,
        orderSearchError,
        setOrderSearchError,
        handleScanProduct,
        itemSearchError,
        setItemSearchError,
        orderContents,
        setOrderContents,
        products,
        handleSubmitPickingSalesOrder,
        handleSubmitPurchaseOrderReceiving,
        orderNumber,
        setOrderNumber,
        inputRef,
        company,
        handleGetProductBin,
        handleSubmitPickingTransferOrder,
        handleSubmitScanningTOFBALabel,
        dailyOrders,
        handleFetchDailyOrders,
        selectedDailyOrderClass,
        setSelectedDailyOrderClass,
        filteredDailyOrdersByClass,
        selectedDailyOrderBatchIndex,
        setSelectedDailyOrderBatchIndex,
        handleScanDailyOrderItem,
        markDailyOrderAsFinished,
        selectedLocationId,
        setSelectedLocationId
      }}
    >
      {children}
    </OrderContext.Provider>
  )
}

export default OrderContextProvider
